import {computed, inject, Injectable, Signal} from '@angular/core';
import {Decimal} from 'decimal.js';
import {capitalize} from 'lodash-es';
import moment from 'moment';
import {formatDateTime} from '../../common/formatters';
import {sortedIndexByComparator} from '../../common/sorted-index-by-comparator';
import {DexComputedTrancheService} from '../../components/dex/dex-streaming/computed/dex-computed-tranche.service';
import {DEX_RATE_REF_SYMBOL} from '../../components/dex/dex-streaming/const';
import {DexMdSubjectService} from '../../components/dex/dex-streaming/subjects/dex-md-subject-service';
import {DexSecuritiesSubjectService} from '../../components/dex/dex-streaming/subjects/dex-securities-subject-service';
import {Deliverable, DeliverableId} from '../../components/dex/dex-streaming/types/deliverable';
import {DEX_ORDER_STATUS_NAMING, DexOrderFromApiStatus} from '../../components/dex/dex-streaming/types/dex-order';
import {
    mathValueWithDelDecJs,
    mathWithDiffDpDecimalJs,
    numToValueWithDel,
    parseDexBigIntStr,
    ValueWithDel,
    ValueWithDelDp
} from '../../components/dex/dex-streaming/utils';
import {DexTrancheUtilityService} from '../../components/dex/dex-tranche-utility/dex-tranche-utility.service';
import {Side} from '../../enums/side';
import {DisplayTxHistoryRec, DisplayTxHistoryRecKey} from './display-tx-history-rec';
import {
    TX_HISTORY_REC_METHOD_NAMING,
    TX_HISTORY_REC_STATUS_NAMING,
    TxHistoryDepositLogsMeta,
    TxHistoryDepositMeta,
    TxHistoryRec,
    TxHistoryRecMethod,
    TxHistoryStatus,
    TxHistoryTransferMeta,
    TxHistoryWithdrawLogsMeta,
    TxHistoryWithdrawMeta
} from './tx-history-rec';
import {DexOrderTx, DexOrderTxOrder, DexOrderTxTx} from './tx-storage/dex-order-tx';

const MAP_ORDER_STATUS_TO_TX_STATUS_FOR_CLASS: Partial<Record<DexOrderFromApiStatus, TxHistoryStatus>> = {
    [DexOrderFromApiStatus.pending]:   TxHistoryStatus.PENDING,
    [DexOrderFromApiStatus.accepted]:  TxHistoryStatus.CONFIRMED,
    [DexOrderFromApiStatus.executed]:  TxHistoryStatus.DONE,
    [DexOrderFromApiStatus.cancelled]: TxHistoryStatus.FAILED
};

@Injectable()
export class DisplayTxHistoryFactory {

    private readonly dexSecuritiesSubjectService = inject(DexSecuritiesSubjectService);
    private readonly dexMdSubjectService = inject(DexMdSubjectService);
    private readonly dexTrancheUtilityService = inject(DexTrancheUtilityService);
    private readonly dexComputedTrancheService = inject(DexComputedTrancheService);

    public create(orderTx: DexOrderTx): DisplayTxHistoryRec {
        return (orderTx as DexOrderTxOrder).order
            ? this.createOrder(orderTx as DexOrderTxOrder)
            : this.createTxOnly(orderTx as DexOrderTxTx);
    }

    public createAll(orderTxs: DexOrderTx[]): DisplayTxHistoryRec[] {
        return orderTxs.map(orderTx => this.create(orderTx));
    }

    public createTxOnly(oTx: DexOrderTxTx): DisplayTxHistoryRec {
        const th = oTx.transaction;
        const createdAtTimestamp = moment.utc(oTx.createdAt || th.createdAt).valueOf();

        const displayTx: DisplayTxHistoryRec = {
            raw:       oTx,
            key:       this.createRowKeyForOrderTx(oTx),
            createdAt: formatDateTime(createdAtTimestamp),
            updatedAt: formatDateTime(oTx.updatedAt || th.updatedAt),
            hash:      th.hash,
            address:   th.from,
            addressTo: th.to,
            method:    th.metadata
                           ? (TX_HISTORY_REC_METHOD_NAMING[th.metadata.method] || th.metadata.method)
                           : '',

            gasUsedNative: this.dexTrancheUtilityService.nativeTokenToVd(
                th.gasUsedNative && BigInt(th.gasUsedNative)
            ),
            status:        TX_HISTORY_REC_STATUS_NAMING[th.status] || th.status,
            statusClass:   `-${th.status.toLowerCase()}`,
            timestamp:     createdAtTimestamp
        };

        const method = th.metadata?.method;

        switch (method) {
            case TxHistoryRecMethod.DEPOSIT: {
                this.addTrancheField(displayTx, th.metadata as TxHistoryDepositMeta);
                this.addDepositDisplayFields(displayTx, th);
                break;
            }
            case TxHistoryRecMethod.WITHDRAW: {
                this.addTrancheField(displayTx, th.metadata as TxHistoryWithdrawMeta);
                this.addWithdrawalDisplayFields(displayTx, th);
                break;
            }
            case TxHistoryRecMethod.TRANSFER: {
                this.addTransferDisplayFields(displayTx, th);
                break;
            }
        }

        return displayTx;
    }

    public createAllTxOnly(orderTxs: DexOrderTxTx[]): DisplayTxHistoryRec[] {
        return orderTxs.map(orderTx => this.createTxOnly(orderTx));
    }

    public createRowKeyForOrderTx(oTx: DexOrderTx): DisplayTxHistoryRecKey {
        const orderId = oTx.order?.orderId || oTx.orderId || undefined;
        const txHash = oTx.transaction?.hash || undefined;
        const trackId = oTx.id || txHash;

        return {trackId, orderId, txHash};
    }

    public compareRowKeys(key1: DisplayTxHistoryRecKey, key2: DisplayTxHistoryRecKey): boolean {
        return key1.txHash && key2.txHash
            ? key1.txHash === key2.txHash
            : key1.orderId && key2.orderId
                ? key1.orderId === key2.orderId
                : key1.trackId === key2.trackId;
    }

    private createOrder(oTx: DexOrderTxOrder): DisplayTxHistoryRec {
        const o = oTx.order;
        const th = oTx.transaction || undefined;
        const histMethod: TxHistoryRecMethod = o.side === Side.BUY
            ? TxHistoryRecMethod.DEPOSIT
            : TxHistoryRecMethod.WITHDRAW;
        const histStatusForClass: TxHistoryStatus | undefined = MAP_ORDER_STATUS_TO_TX_STATUS_FOR_CLASS[o.status];

        const $tradable = computed(() => {
            this.dexSecuritiesSubjectService.$tradablesUpdated();

            return this.dexSecuritiesSubjectService.tradableMapById.get(o.tradeableEntityId);
        });

        const $tranche = computed(() => {
            this.dexMdSubjectService.$tranchesUpdated();
            const tradable = $tradable();

            return tradable && this.dexMdSubjectService.trancheMapByDelId.get(tradable.quantityDeliverableId);
        });

        const $computedTranche$ = computed(() => $tranche() && this.dexComputedTrancheService.create($tranche()));

        const $dSent: Signal<Deliverable> = computed(() => {
            this.dexSecuritiesSubjectService.$deliverablesUpdated();
            const tradable = $tradable();

            const dId: DeliverableId = o.side === Side.BUY
                ? tradable.priceDeliverableId
                : tradable.quantityDeliverableId;

            return this.dexSecuritiesSubjectService.getDeliverableById(dId);
        });

        const $tradesInfo = computed(() => {
            this.dexSecuritiesSubjectService.$deliverablesUpdated();
            this.dexSecuritiesSubjectService.$tradablesUpdated();

            return this.readOrderTrades(oTx);
        });

        const $amount = computed(() => {
            const tradesInfo = $tradesInfo();

            let result: ValueWithDelDp | undefined = (o.side === Side.BUY)
                ? tradesInfo.assetAmounts?.[0]
                : tradesInfo.trancheQty;

            if (!result) {
                result = {d: $dSent(), v: parseDexBigIntStr(o.quantity)};
            }

            return result;
        });

        const $received = computed<ValueWithDelDp[] | undefined>(() => {
            const tradesInfo = $tradesInfo();

            return (o.side === Side.BUY)
                ? tradesInfo.trancheQty && [tradesInfo.trancheQty]
                : tradesInfo.assetAmounts;
        });

        const $price = computed(() => {
            const tradesInfo = $tradesInfo();

            return tradesInfo.refPrice;
        });

        const createdAtTimestamp = moment.utc(o.createdAt).valueOf();

        return {
            raw: oTx,
            key: this.createRowKeyForOrderTx(oTx),

            createdAt:     formatDateTime(createdAtTimestamp),
            updatedAt:     formatDateTime(o.executedAt || createdAtTimestamp),
            $tranche:      $computedTranche$,
            orderId:       o.orderId,
            $amount,
            $received,
            $price,
            $fee:          undefined, // TODO
            hash:          th?.hash,
            address:       o.address,
            method:        TX_HISTORY_REC_METHOD_NAMING[histMethod] || histMethod,
            gasUsedNative: th && this.dexTrancheUtilityService.nativeTokenToVd(
                th.gasUsedNative && BigInt(th.gasUsedNative)
            ),
            status:        o.status ? DEX_ORDER_STATUS_NAMING[o.status] || capitalize(o.status) : '',
            statusClass:   histStatusForClass ? `-${histStatusForClass.toLowerCase()}` : '',
            timestamp:     createdAtTimestamp
        } satisfies DisplayTxHistoryRec;
    }

    private addTrancheField(
        displayTx: DisplayTxHistoryRec,
        metadata?: {trancheId?: number | string}
    ): void {
        if (metadata?.trancheId) {
            displayTx.$tranche = computed(() => {
                this.dexMdSubjectService.$tranchesUpdated();

                const trancheId = Number(metadata.trancheId);
                const tranche = this.dexMdSubjectService.trancheMapById.get(trancheId);

                return tranche && this.dexComputedTrancheService.create(tranche);
            });
        }
    }

    private readOrderTrades(oTx: DexOrderTxOrder): {
        refPrice: ValueWithDelDp | undefined; // Trade with *-USD tradable
        trancheQty: ValueWithDelDp | undefined;
        assetAmounts: ValueWithDel[] | undefined;
    } {
        let refPrice: ValueWithDelDp | undefined;
        let trancheQty: ValueWithDelDp | undefined;
        let assetAmounts: ValueWithDel[] | undefined;

        if (oTx.trades?.length) {
            const refDelId = this.getRefDeliverable()?.deliverableId;

            for (const trade of oTx.trades) {
                const tradable = this.dexSecuritiesSubjectService.tradableMapById.get(trade.tradeableEntityId);
                const d = this.dexSecuritiesSubjectService.deliverableMapById.get(tradable.priceDeliverableId);

                if (d) {
                    if (tradable && tradable.priceDeliverableId === refDelId) {
                        refPrice = {
                            d,
                            v: parseDexBigIntStr(trade.execPrice),
                            dp: tradable.priceDecimalPlaces
                        };
                        trancheQty = {
                            d: this.dexSecuritiesSubjectService.deliverableMapById.get(tradable.quantityDeliverableId),
                            v: parseDexBigIntStr(trade.execQuantity),
                            dp: tradable.quantityDecimalPlaces
                        };
                    } else {
                        // actualQty = execQty * execPrice (it's =< order.quantity)
                        const v = mathWithDiffDpDecimalJs(
                            parseDexBigIntStr(trade.execQuantity),
                            tradable.quantityDecimalPlaces,
                            parseDexBigIntStr(trade.execPrice),
                            tradable.priceDecimalPlaces,
                            d.decimalPlaces,
                            (a, b) => a.mul(b),
                            Decimal.ROUND_DOWN
                        );

                        const assetQty: ValueWithDel = {d, v};

                        if (assetAmounts?.length) {
                            const sortedIndex = sortedIndexByComparator(
                                assetAmounts,
                                assetQty,
                                (a, b) =>
                                    a.d.symbol.localeCompare(b.d.symbol, undefined, {numeric: true}),
                                true // Append the same to the end
                            );

                            let isMerged: boolean = false;

                            // Sum values with the same deliverable.
                            // It's required, due to the tracking by deliverableId in @for-of in templates.
                            if (sortedIndex > 0) {
                                const prevItem = assetAmounts[sortedIndex - 1];

                                if (prevItem.d.deliverableId === assetQty.d.deliverableId) {
                                    prevItem.v = prevItem.v + assetQty.v;
                                    isMerged = true;
                                }
                            }

                            if (!isMerged) {
                                assetAmounts.splice(sortedIndex, 0, assetQty);
                            }
                        } else {
                            assetAmounts = [{d, v}];
                        }
                    }
                }
            }
        }

        return {
            refPrice,
            trancheQty,
            assetAmounts
        };
    }

    private addDepositDisplayFields(displayTx: DisplayTxHistoryRec, th: TxHistoryRec): void {
        const depositMeta = th.metadata as TxHistoryDepositMeta;
        const logsMeta = th.logsMeta as TxHistoryDepositLogsMeta;

        const populateZeroReceivedFromMeta = () => {
            const $trReceived = computed(() => {
                this.dexMdSubjectService.$tranchesUpdated();
                const trId = Number(depositMeta.trancheId);

                return this.dexMdSubjectService.trancheMapById.get(trId);
            });

            const $dReceived = computed(() => {
                this.dexSecuritiesSubjectService.$deliverablesUpdated();
                const tr = $trReceived();

                let d: Deliverable | undefined;

                if (tr) {
                    const dId: DeliverableId = tr.unitDeliverableId;
                    d = this.dexSecuritiesSubjectService.getDeliverableById(dId);
                }

                return d;
            });

            displayTx.$received = computed((): ValueWithDelDp[] => {
                const d = $dReceived();

                return [{d, v: undefined}];
            });
        };

        if (logsMeta) {
            const depositEvent = logsMeta.depositEvents?.[0];
            const orderEnteredEvent = logsMeta.orderEnteredEvent;

            if (orderEnteredEvent) {
                displayTx.orderId = orderEnteredEvent.orderId;

                const $dSent: Signal<Deliverable> = computed(() => {
                    this.dexSecuritiesSubjectService.$deliverablesUpdated();
                    const dId: DeliverableId = Number(orderEnteredEvent.deliverableId);

                    return this.dexSecuritiesSubjectService.getDeliverableById(dId);
                });

                displayTx.$amount = computed(() => {
                    const d = $dSent();

                    return numToValueWithDel(orderEnteredEvent.quantityDec, d);
                });

                populateZeroReceivedFromMeta();
            } else if (depositEvent) {
                const sentRec = depositEvent.sent;
                const receivedRec = depositEvent.received;

                const $dSent: Signal<Deliverable> = computed(() => {
                    this.dexSecuritiesSubjectService.$deliverablesUpdated();
                    const dId: DeliverableId = Number(sentRec.deliverableId);

                    return this.dexSecuritiesSubjectService.getDeliverableById(dId);
                });

                displayTx.$amount = computed(() => {
                    const d = $dSent();
                    const v = BigInt(sentRec.assetQuantity);

                    return {d, v};
                });

                const $trReceived = computed(() => {
                    this.dexMdSubjectService.$tranchesUpdated();
                    const trId = Number(receivedRec.trancheId);

                    return this.dexMdSubjectService.trancheMapById.get(trId);
                });

                const $dReceived = computed(() => {
                    this.dexSecuritiesSubjectService.$deliverablesUpdated();
                    const tr = $trReceived();

                    let d: Deliverable | undefined;

                    if (tr) {
                        const dId: DeliverableId = tr.unitDeliverableId;
                        d = this.dexSecuritiesSubjectService.getDeliverableById(dId);
                    }

                    return d;
                });

                displayTx.$received = computed(() => {
                    const d = $dReceived();
                    const v = BigInt(receivedRec.qty);

                    return [{d, v}];
                });

                displayTx.$fee = computed(() => {
                    const dRef = this.getRefDeliverable();

                    return {d: dRef, v: parseDexBigIntStr(depositEvent.feeUsd)};
                });

                displayTx.$price = computed(() =>
                    mathValueWithDelDecJs(
                        displayTx.$received()[0],
                        displayTx.$amount(),
                        $dSent(),
                        (recv, s) => s.isZero() ? new Decimal(0) : recv.div(s)
                    )
                );
            }
        } else {
            const $dSent: Signal<Deliverable> = computed(() => {
                this.dexSecuritiesSubjectService.$deliverablesUpdated();
                const dId: DeliverableId = Number(depositMeta.deliverableId);

                return this.dexSecuritiesSubjectService.getDeliverableById(dId);
            });

            displayTx.$amount = computed(() => {
                const d = $dSent();
                const v = BigInt(depositMeta.assetQtyToDeposit);

                return {d, v};
            });

            // Don't add received for the transaction, which has not been yet sent to the blockchain ("bg-exec").
            if (displayTx.raw.transaction?.hash || displayTx.raw.order) {
                populateZeroReceivedFromMeta();
            }
        }
    }

    private addWithdrawalDisplayFields(displayTx: DisplayTxHistoryRec, th: TxHistoryRec): void {
        const withdrawMeta = th.metadata as TxHistoryWithdrawMeta;
        const logsMeta = th.logsMeta as TxHistoryWithdrawLogsMeta;

        const $trSent = computed(() => {
            this.dexMdSubjectService.$tranchesUpdated();
            const trId = Number(withdrawMeta.trancheId);

            return this.dexMdSubjectService.trancheMapById.get(trId);
        });

        const $dSent: Signal<Deliverable> = computed(() => {
            this.dexSecuritiesSubjectService.$deliverablesUpdated();
            const dId: DeliverableId = Number($trSent().unitDeliverableId);

            return this.dexSecuritiesSubjectService.getDeliverableById(dId);
        });

        if (logsMeta) {
            const withdrawEvent = logsMeta.withdrawalEvents?.[0];
            const lpTokenBurntEvent = logsMeta.lpTokenBurntEvents?.[0];
            const orderEnteredEvent = logsMeta.orderEnteredEvent;

            if (orderEnteredEvent) {
                displayTx.orderId = orderEnteredEvent.orderId;

                const $dSent: Signal<Deliverable> = computed(() => {
                    this.dexSecuritiesSubjectService.$deliverablesUpdated();
                    const dId: DeliverableId = Number(orderEnteredEvent.deliverableId);

                    return this.dexSecuritiesSubjectService.getDeliverableById(dId);
                });

                displayTx.$amount = computed(() => {
                    const d = $dSent();

                    return numToValueWithDel(orderEnteredEvent.quantityDec, d);
                });

                // populateZeroReceivedFromMeta(); // TODO
            } else if (withdrawEvent) {
                displayTx.$amount = computed(() => {
                    const d = $dSent();
                    const v = BigInt(withdrawEvent.lptQuantityToBurn);

                    return {d, v};
                });

                const $dReceived: Signal<Deliverable[]> = computed(() => {
                    this.dexSecuritiesSubjectService.$deliverablesUpdated();

                    return (withdrawEvent.assetsWithdrawn || []).map(a =>
                        this.dexSecuritiesSubjectService.getDeliverableById(Number(a.deliverableId))
                    );
                });

                displayTx.$received = computed(() => {
                    const ds = $dReceived();

                    return (withdrawEvent.assetsWithdrawn || []).map((a, i) => ({
                        d: ds[i],
                        v: BigInt(a.assetQuantity)
                    }));
                });

                displayTx.$fee = computed(() => {
                    const dRef = this.getRefDeliverable();

                    return {d: dRef, v: parseDexBigIntStr(withdrawEvent.withdrawalFeeUsd)};
                });

                if (withdrawEvent.assetsWithdrawn?.length === 1) {
                    displayTx.$price = computed(() =>
                        mathValueWithDelDecJs(
                            displayTx.$amount(),
                            displayTx.$received()[0],
                            displayTx.$received()[0].d,
                            (s, recv) => recv.isZero() ? new Decimal(0) : s.div(recv)
                        )
                    );
                } else if (lpTokenBurntEvent) {
                    displayTx.$price = computed(() => {
                        const refDel = this.getRefDeliverable();

                        return {d: refDel, v: parseDexBigIntStr(lpTokenBurntEvent.lptPrice)};
                    });
                }
            }
        } else {
            displayTx.$amount = computed(() => {
                const d = $dSent();
                const v = BigInt(withdrawMeta.lptQuantityToBurn);

                return {d, v};
            });
        }
    }

    private addTransferDisplayFields(displayTx: DisplayTxHistoryRec, th: TxHistoryRec): void {
        const meta = th.metadata as TxHistoryTransferMeta;

        const $dSent: Signal<Deliverable> = computed(() => {
            this.dexSecuritiesSubjectService.$deliverablesUpdated();
            const dId: DeliverableId = Number(meta.deliverableId);

            return this.dexSecuritiesSubjectService.getDeliverableById(dId);
        });

        displayTx.$amount = computed(() => {
            const d = $dSent();

            return numToValueWithDel(meta.amount, d);
        });

        displayTx.showBothAddresses = true;
    }

    private getRefDeliverable(): Deliverable {
        return this.dexSecuritiesSubjectService.getDeliverableBySymbol(DEX_RATE_REF_SYMBOL);
    }

}
